<script lang="ts">
  /** THE LOOP (G6b — the Chair: "We need a way to view the npc loop and the decision tree"; frame 7 of his Free Guy stills: Guy's day
   *  as a CIRCUIT — WAKE UP → GREET GOLDFISH → … → SLEEP — pills on a wire, the live one lit). Its graph drawn the same way: its states
   *  as pills round a loop (the initial top-left), every transition a wire labelled with what fires it, "any state" wires from the hub.
   *  Live: the state it is in glows; the wires it has travelled lately thicken, the last one green; under it, its last steps with what
   *  moved each — an object's behaviour shown inside its loop (its graph waits in the state that handed over: `inside` names it). */
  import type { LoopStep } from '../types'

  type State = { name: string; lod?: number }
  type Tr = { from: string; to: string; trigger: { kind: string; param?: number; topic?: string } }
  let { states, transitions, initial, current = null, inside = null, loop = [] }: { states: State[]; transitions: Tr[]; initial: string; current?: string | null; inside?: string | null; loop?: LoopStep[] } = $props()

  const W = 460, H = 300, CX = W / 2, CY = H / 2 + 4, RX = 165, RY = 98
  const order = $derived([...states].sort((a, b) => (a.name === initial ? -1 : b.name === initial ? 1 : 0)))
  // pills round the circuit (an oval track — the initial top-left, clockwise)
  const pos = $derived(Object.fromEntries(order.map((s, i) => {
    const a = -Math.PI * 0.72 + (i / order.length) * Math.PI * 2
    return [s.name, { x: CX + Math.cos(a) * RX, y: CY + Math.sin(a) * RY, hw: 14 + s.name.length * 4.1, hh: 11 }]
  })) as Record<string, { x: number; y: number; hw: number; hh: number }>)
  const short = (t: Tr['trigger']) => (t.kind === 'withinDistance' ? `within ${t.param}` : t.kind === 'beyondDistance' ? `beyond ${t.param}` : `${t.kind}${t.param != null ? ' ' + t.param : ''}${t.topic && t.topic !== '*' ? ' ' + t.topic : t.topic === '*' ? ' *' : ''}`)
  // its own graph's steps (a behaviour's steps are drawn in the timeline, not on its graph)
  const own = $derived(loop.filter((s) => !s.inside))
  const used = $derived(own.reduce<Record<string, number>>((m, s) => { const k = `${s.from}→${s.to}`; m[k] = (m[k] ?? 0) + 1; return m }, {}))
  const last = $derived(own.length ? `${own[own.length - 1].from}→${own[own.length - 1].to}` : '')
  /** an arrow from a to b (bent when the pair runs both ways, so the two don't overlap), ending at the node's rim */
  function arrow(a: { x: number; y: number; hw?: number; hh?: number }, b: { x: number; y: number; hw?: number; hh?: number }, bend: number) {
    const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1, nx = -dy / d, ny = dx / d
    const mx = (a.x + b.x) / 2 + nx * bend, my = (a.y + b.y) / 2 + ny * bend
    // to the pill's edge along the wire (its half width / half height), a hair beyond
    const rim = (p: { hw?: number; hh?: number }, vx: number, vy: number) => { const hw = (p.hw ?? 15) + 3, hh = (p.hh ?? 15) + 3; return Math.min(Math.abs(vx) > 1e-6 ? hw / Math.abs(vx) : 1e9, Math.abs(vy) > 1e-6 ? hh / Math.abs(vy) : 1e9) }
    const pull = (p: { x: number; y: number; hw?: number; hh?: number }, q: { x: number; y: number }) => { const vx = q.x - p.x, vy = q.y - p.y, l = Math.hypot(vx, vy) || 1, r = rim(p, vx / l, vy / l); return { x: p.x + (vx / l) * r, y: p.y + (vy / l) * r } }
    const s = pull(a, { x: mx, y: my }), e = pull(b, { x: mx, y: my })
    return { d: `M${s.x.toFixed(1)},${s.y.toFixed(1)} Q${mx.toFixed(1)},${my.toFixed(1)} ${e.x.toFixed(1)},${e.y.toFixed(1)}`, lx: mx, ly: my }
  }
  // one wire per pair of states, every trigger that runs it stacked on its label (choosing → idle: delay 30 · exception *)
  const edges = $derived.by(() => {
    const pairs = new Map<string, Tr[]>()
    for (const t of transitions) if (t.from !== '*' && pos[t.from] && pos[t.to]) pairs.set(`${t.from}→${t.to}`, [...(pairs.get(`${t.from}→${t.to}`) ?? []), t])
    return [...pairs].map(([k, ts]) => {
      const both = pairs.has(`${ts[0].to}→${ts[0].from}`)
      return { ...arrow(pos[ts[0].from], pos[ts[0].to], both ? 22 : 10), labels: ts.map((t) => short(t.trigger)), n: used[k] ?? 0, last: k === last, key: k }
    })
  })
  const anys = $derived(transitions.filter((t) => t.from === '*' && pos[t.to]).map((t) => ({ ...arrow({ x: CX, y: CY }, pos[t.to], 0), label: short(t.trigger), to: t.to })))
  const hhmmss = (iso: string) => new Date(iso).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
</script>

<svg class="loop" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Its loop: the graph, the state it is in, the transitions it has taken">
  <defs>
    <marker id="ah" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L8,4 L0,8 z" fill="#3a6b80" /></marker>
    <marker id="ah-on" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L8,4 L0,8 z" fill="#22d3ee" /></marker>
    <marker id="ah-last" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L8,4 L0,8 z" fill="#19c37d" /></marker>
  </defs>
  {#each edges as e (e.key)}
    <path d={e.d} class="edge" class:on={e.n > 0} class:last={e.last} style={e.n ? `stroke-width:${Math.min(5, 1.2 + e.n * 0.5)}` : ''}
      marker-end={e.last ? 'url(#ah-last)' : e.n ? 'url(#ah-on)' : 'url(#ah)'} />
    {#each e.labels as l, j}<text x={e.lx} y={e.ly - 3 + (j - (e.labels.length - 1)) * 9.5} class="lbl" class:on={e.n > 0}>{l}{e.n && j === e.labels.length - 1 ? ` ×${e.n}` : ''}</text>{/each}
  {/each}
  {#if anys.length}
    <circle cx={CX} cy={CY} r="15" class="any" /><text x={CX} y={CY + 3.5} class="anyt">ANY</text>
    {#each anys as e}<path d={e.d} class="edge any-e" marker-end="url(#ah)" /><text x={e.lx} y={e.ly - 3} class="lbl">{e.label}</text>{/each}
  {/if}
  {#each order as s (s.name)}
    {@const p = pos[s.name]}
    <g class="node" class:now={current === s.name} class:initial={s.name === initial}>
      {#if current === s.name}<rect x={p.x - p.hw + 6} y={p.y - p.hh - 5} width={p.hw * 0.7} height="3" class="tab" />{/if}
      <rect x={p.x - p.hw} y={p.y - p.hh} width={p.hw * 2} height={p.hh * 2} rx="3" />
      <path d={`M${p.x - p.hw + 7},${p.y - 3} l4,3 l-4,3 z`} class="play" />
      <text x={p.x + 4} y={p.y + 4}>{s.name.toUpperCase()}</text>
      <text x={p.x} y={p.y + p.hh + 11} class="lod">L{s.lod ?? 1}{s.name === initial ? ' · start' : ''}</text>
      {#if current === s.name && inside}<text x={p.x} y={p.y + p.hh + 21} class="inside">↳ {inside}</text>{/if}
    </g>
  {/each}
</svg>
{#if loop.length}
  <ol class="steps mono" aria-label="Its last steps">
    {#each [...loop].reverse().slice(0, 10) as s, i}
      <li class:first={i === 0} class:inside={!!s.inside}>
        <span class="t">{hhmmss(s.at)}</span>
        {#if s.inside}<span class="in">{s.inside} ▸</span>{/if}
        <span class="ft">{s.from} <b>→</b> {s.to}</span><span class="on">{s.on}</span>
      </li>
    {/each}
  </ol>
{/if}

<style>
  .loop { width: 100%; max-height: 330px; display: block; }
  .edge { fill: none; stroke: #2b4a59; stroke-width: 1.2; }
  .edge.on { stroke: #22d3ee; opacity: .85; }
  .edge.last { stroke: #19c37d; opacity: 1; }
  .any-e { stroke-dasharray: 3 3; }
  .lbl { font: 8.5px var(--mono); fill: #5d7684; text-anchor: middle; }
  .lbl.on { fill: #9fe8f5; }
  .any { fill: #0d1419; stroke: #2b4a59; }
  .anyt { font: 600 8px var(--mono); fill: #5d7684; text-anchor: middle; }
  .node rect { fill: #0e1a17; stroke: #1f6b4f; stroke-width: 1.4; }
  .node.initial rect { stroke: #22d3ee; }
  .node .play { fill: #e5484d; }
  .node text { font: 700 11px var(--display); fill: #dff3ea; text-anchor: middle; letter-spacing: .04em; }
  .node .lod { font: 8px var(--mono); fill: var(--muted); font-weight: 400; letter-spacing: 0; }
  .node.now rect { fill: #0f2e22; stroke: #19c37d; stroke-width: 2.2; filter: drop-shadow(0 0 7px rgba(25, 195, 125, .7)); }
  .node .tab { fill: #19c37d; stroke: none; }
  .node.now text { fill: #eafff4; }
  .node .inside { font: 600 8.5px var(--mono); fill: var(--amber); letter-spacing: 0; }
  .edge { filter: drop-shadow(0 0 2px rgba(34, 211, 238, .25)); }
  .steps { list-style: none; margin: 6px 0 0; padding: 0; font-size: 11px; max-height: 190px; overflow: auto; }
  .steps li { display: grid; grid-template-columns: 58px auto 1fr auto; gap: 8px; padding: 2px 6px; border-left: 2px solid var(--line-dim); color: #9fb0bb; }
  .steps li.first { border-left-color: var(--green); color: var(--text); }
  .steps li.inside { border-left-color: var(--amber); }
  .steps .t { color: var(--faint); }
  .steps .in { color: var(--amber); }
  .steps .ft b { color: var(--cyan); font-weight: 500; }
  .steps .on { color: var(--muted); text-align: right; }
</style>
